import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Button, Card, PageHeader, Badge, Modal, Input } from '../../components/ui/UI';
import { Plus, Trash2, Pencil, RefreshCw, X, Check } from 'lucide-react';
import './FormFieldsPage.css';

const DEFAULT_FIELDS = {
  shift: { displayName: 'Shift', options: ['Day', 'Night'] },
  location: { displayName: 'Observation Location', options: ['Head Office - 1st Floor', 'Head Office - 2nd Floor', 'Truck Park/Waiting Area', 'Raw Material Warehouse', 'Powerhouse/Generator Area', 'Kitchen/Canteen', 'Laundry', 'Quality Laboratory', 'Seasoning Room/Meat Room', 'Rondo 1 - 2', 'Rondo 3 - 4', 'Rondo 5', 'Oven', 'Snacks Wrapping Section', 'Coconut Production/Wrapping', 'Pastry Dough Mixing', 'Pastry Mixing Room', 'Pastry Kitchen', 'Bread Mixing Room', 'Engineering Workshop', 'Engineering Store', 'Loading Bay', 'Waste Area/ETP', 'Dairies Production Office', 'Ambient Warehouse', 'Compressor Workshop', 'Pouch/Waltzer Section', 'Dairies Mixing Room', 'Bowl Washing/Bulk Section', 'Dairies Engineering Office/Gas Generator House', 'Dairies Boiler House', 'SWAN Filling Room', 'Generator Area/Gate House', 'SWAN Production Dry End', 'Entire Factory', 'SWAN Quality Laboratory/Entrance Control', 'Preform Tipping/Staging Area', 'Pet Crush Site', 'SWAN Contanerized Storage', 'Clinic', 'Pallet Repair Section'] },
  category: { displayName: 'Behavior Category', options: ['PPE Usage & Compliance', 'PPE Condition & Maintenance', 'Specialized PPE (food-grade / cold-store)', 'Manual Handling & Lifting Technique', 'Repetitive Motion & Posture', 'Work Positioning & Reach', 'Lockout/Tagout (LOTO) Compliance', 'Machine Guarding & Interlocks', 'Equipment Inspection & Pre-use Checks', 'Conveyor & Automated Line Interaction', 'Work Area Cleanliness & Organisation', 'Spill Management & Clean-up', 'Aisle & Walkway Clearance', 'Waste Segregation & Disposal', 'Cleaning Agent & Sanitiser Handling', 'Food-grade Chemical Storage & Labelling', 'CO₂/Refrigerant Gas Safety', 'Ladder & Platform Use', 'Fall Prevention & Arrest Systems', 'Overhead Work & Falling Object Controls', 'Electrical Panel & Switchgear Access', 'Temporary Wiring & Extension Leads', 'Forklift/MHE Awareness & Right-of-Way', 'Pedestrian Lane Discipline', 'Loading Dock & Yard Safety', 'Hand Hygiene & Gowning Compliance', 'Foreign Body Control (FBC) Adherence', 'Allergen Segregation Practice', 'Cold Chain & Temperature Discipline', 'PTW Compliance & Authorisation', 'Standard Operating Procedure (SOP) Adherence', 'Hot Work & Confined Space Controls', 'Compressed Air Usage', 'Steam & Hot Water Systems', 'Refrigeration/Ammonia Plant Behaviour', 'Fire Exit & Emergency Route Behaviour', 'Fire Extinguisher Access & Usage', 'Emergency Response Readiness', 'Safety Communication & Reporting Culture', 'Coaching & Intervention Behaviour', 'Fatigue & Alertness Management', 'Compliance with Safety Instructions'] },
  antecedent: { displayName: 'Antecedent', options: ['Inadequate or absent safety training', 'New/transferred employee unfamiliar with task', 'Misunderstanding of SOP or work instruction', 'Low safety literacy or language barrier', 'Overconfidence/complacency from routine', 'Risk under-estimation ("it won\'t happen to me")', 'Peer pressure/social normalisation of risk', 'Desire to demonstrate capability or speed', 'Low personal risk perception for the task', 'Production target/schedule pressure', 'Understaffing or absent colleague cover', 'Short-cut taken to save time', 'End-of-shift fatigue or rushing', 'Poor housekeeping or cluttered workspace', 'Inadequate lighting or signage', 'Equipment design promotes unsafe behaviour', 'Extreme temperature (cold store/oven area)', 'Noise reducing verbal communication', 'PPE unavailable, wrong size, or uncomfortable', 'Defective or unserviceable equipment used', 'Correct tool not available – improvisation used', 'Inadequate safety guards or controls', 'No procedure exists for the task', 'Outdated or unclear SOP', 'Procedure impractical – routinely bypassed', 'PTW/LOTO system not enforced', 'Absent or insufficient supervision', 'Previous unsafe behaviour went unaddressed', 'Supervisor modelled or condoned unsafe act', 'Low visible felt leadership on the floor', 'Safety feedback culture is weak or punitive', 'Production over safety trade-off normalised', 'Low team morale or engagement', 'Safety incentives create under-reporting bias', 'High staff turnover reducing safety knowledge', 'Contractor/visitor unfamiliar with site rules', 'Fatigue (shift duration, poor rest)', 'Physical incapacity or health condition', 'Distraction (phone, personal concern)', 'Rushing due to personal urgency', 'Effective safety training & competency building', 'Strong visible felt leadership by management', 'Positive peer safety culture & coaching', 'Clear, accessible, and practical procedures', 'Adequate supervision and feedback loop', 'Good availability and fit of PPE', 'Intrinsic safety motivation & ownership'] }
};

export default function FormFieldsPage() {
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedField, setSelectedField] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const fetchFields = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/form-fields');
      setFields(data);
    } catch (err) {
      // Initialize with default fields if not in database
      setFields(DEFAULT_FIELDS);
      toast.info('Loading default form fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const handleSaveField = async (fieldName, options) => {
    if (options.length === 0) {
      toast.error('Field must have at least one option');
      return;
    }
    
    setSaving(true);
    try {
      const displayName = DEFAULT_FIELDS[fieldName]?.displayName || fieldName;
      await api.put(`/form-fields/${fieldName}`, { options, displayName });
      setFields(prev => ({ ...prev, [fieldName]: options }));
      toast.success(`${displayName} updated`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving field');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.trim()) {
      toast.error('Option cannot be empty');
      return;
    }

    if (fields[selectedField]?.includes(newOption.trim())) {
      toast.error('Option already exists');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/form-fields/${selectedField}/options`, { option: newOption.trim() });
      setFields(prev => ({
        ...prev,
        [selectedField]: [...prev[selectedField], newOption.trim()]
      }));
      setNewOption('');
      toast.success('Option added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error adding option');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOption = async (option) => {
    setSaving(true);
    try {
      await api.delete(`/form-fields/${selectedField}/options`, { data: { option } });
      setFields(prev => ({
        ...prev,
        [selectedField]: prev[selectedField].filter(opt => opt !== option)
      }));
      toast.success('Option removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error removing option');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOption = async (oldOption) => {
    if (!editingValue.trim()) {
      toast.error('Option cannot be empty');
      return;
    }

    if (editingValue === oldOption) {
      setEditingOption(null);
      setEditingValue('');
      return;
    }

    if (fields[selectedField]?.includes(editingValue.trim())) {
      toast.error('Option already exists');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/form-fields/${selectedField}/options`, { oldOption, newOption: editingValue.trim() });
      setFields(prev => ({
        ...prev,
        [selectedField]: prev[selectedField].map(opt => opt === oldOption ? editingValue.trim() : opt)
      }));
      setEditingOption(null);
      setEditingValue('');
      toast.success('Option updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating option');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fade-in">
        <PageHeader title="Form Dropdowns" subtitle="Manage form field options" />
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />)}
          </div>
        </Card>
      </div>
    );
  }

  const fieldEntries = Object.entries(fields).map(([key, options]) => ({
    key,
    displayName: DEFAULT_FIELDS[key]?.displayName || key,
    optionCount: Array.isArray(options) ? options.length : 0
  }));

  return (
    <div className="fade-in">
      <PageHeader
        title="Form Dropdowns"
        subtitle="Manage form field options for observation submissions"
        action={<Button variant="ghost" size="sm" icon={<RefreshCw size={13} />} onClick={fetchFields}>Refresh</Button>}
      />

      <div className="form-fields-grid">
        {fieldEntries.map(field => (
          <Card key={field.key} className="form-field-card" onClick={() => { setSelectedField(field.key); setModalOpen(true); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: 600 }}>{field.displayName}</h3>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-3)' }}>ID: {field.key}</p>
              </div>
              <Badge variant="accent">{field.optionCount}</Badge>
            </div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.5 }}>
              {Array.isArray(fields[field.key]) ? fields[field.key].slice(0, 2).join(', ') : ''}
              {fields[field.key]?.length > 2 ? '...' : ''}
            </p>
            <button style={{ marginTop: 12, padding: '6px 12px', fontSize: '0.8rem', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              Edit Options →
            </button>
          </Card>
        ))}
      </div>

      {/* Edit Modal */}
      <Modal open={modalOpen} onClose={() => {
        setModalOpen(false);
        setEditingOption(null);
        setEditingValue('');
        setNewOption('');
      }} title={`Edit ${DEFAULT_FIELDS[selectedField]?.displayName || selectedField}`}>
        {selectedField && (
          <div className="modal-form">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '0.85rem', fontWeight: 500 }}>Add New Option</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  placeholder="Enter new option..."
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                />
                <Button size="sm" icon={<Plus size={13} />} onClick={handleAddOption} loading={saving}>Add</Button>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '0.85rem', fontWeight: 500 }}>Current Options ({fields[selectedField]?.length || 0})</label>
              <div className="options-list">
                {Array.isArray(fields[selectedField]) && fields[selectedField].map(option => (
                  <div key={option} className="option-item">
                    {editingOption === option ? (
                      <>
                        <Input
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleEditOption(option)}
                          autoFocus
                          style={{ flex: 1 }}
                        />
                        <button
                          className="edit-confirm-btn"
                          onClick={() => handleEditOption(option)}
                          disabled={saving}
                          title="Confirm edit"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          className="edit-cancel-btn"
                          onClick={() => {
                            setEditingOption(null);
                            setEditingValue('');
                          }}
                          disabled={saving}
                          title="Cancel edit"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{option}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setEditingOption(option);
                              setEditingValue(option);
                            }}
                            disabled={saving}
                            title="Edit option"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveOption(option)}
                            disabled={saving || fields[selectedField].length === 1}
                            title="Remove option"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
